// Hamburger button + left navigation drawer, mirroring the home-hub tiles.
// Pattern ported from store-frontend/src/components/shop/shop-menu.tsx.

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, LogOut, Menu } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { HUB_SECTIONS } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { userInitials } from "@/components/user-menu";

export function AppMenu() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { displayName, username, signOut } = useAuth();

  function handleSignOut() {
    // signOut() also ends the org-wide ICM session and navigates away.
    signOut();
  }

  const entries = [
    { href: "/", label: "Dashboard", icon: LayoutDashboard },
    ...HUB_SECTIONS.map((s) => ({ href: s.href, label: s.label, icon: s.icon })),
  ];

  return (
    <>
      <Button variant="ghost" size="icon-sm" aria-label="Menu" onClick={() => setOpen(true)}>
        <Menu className="size-5" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72">
          <SheetHeader className="pr-10">
            <div className="flex items-center gap-3">
              <Avatar size="lg">
                <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                  {userInitials(displayName, username)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <SheetTitle className="truncate">{displayName || username || "Signed in"}</SheetTitle>
                <SheetDescription className="truncate text-xs">{username ?? ""}</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4 pt-0">
            {entries.map((i) => {
              const active = i.href === "/" ? pathname === "/" : pathname.startsWith(i.href);
              return (
                <SheetClose
                  key={i.href}
                  nativeButton={false}
                  render={
                    <Link
                      to={i.href}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                        active ? "bg-accent font-medium text-accent-foreground" : "hover:bg-accent"
                      )}
                    >
                      <i.icon className="size-4 text-muted-foreground" />
                      {i.label}
                    </Link>
                  }
                />
              );
            })}

            <Separator className="my-2" />

            <SheetClose
              render={
                <Button
                  variant="ghost"
                  onClick={handleSignOut}
                  className="h-auto w-full justify-start gap-3 px-3 py-2.5 text-sm font-normal text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <LogOut className="size-4" /> Sign out
                </Button>
              }
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
